import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';

const PROGRAMS = [
  { slug: 'kids-entrepreneurship', icon: '💡', color: 'linear-gradient(135deg, #FF6B35, #FFD166)', key: 'kidsEntrepreneurship', active: true, image: '/assets/week1-thumb.jpg' },
  { slug: 'ai-literacy', icon: '🤖', color: 'linear-gradient(135deg, #06B6D4, #06D6A0)', key: 'aiLiteracy', active: true, image: '/assets/week2-thumb.jpg' },
  { slug: 'public-presentation', icon: '🎤', color: 'linear-gradient(135deg, #A855F7, #EC4899)', key: 'publicPresentation', active: true, image: '/assets/week3-thumb.jpg' },
  { slug: 'esports', icon: '🎮', color: 'linear-gradient(135deg, #06B6D4, #118AB2)', key: 'eSports', active: true, image: '/assets/week4-thumb.jpg' },
  { slug: 'vedic-mathematics', icon: '🔢', color: 'linear-gradient(135deg, #F59E0B, #EF4444)', key: 'vedicMathematics', active: true, image: '/assets/week5-thumb.jpg' },
  { slug: 'robotics', icon: '🦾', color: 'linear-gradient(135deg, #10B981, #06B6D4)', key: 'robotics', active: true, image: '/assets/week5-thumb.jpg' },
];

const FLOATS = ['🌺', '🌊', '🏄', '🌴', '⭐', '💡', '🎯', '🚀', '🌈', '🎨'];

const SCHEDULE = [
  { time: '9:00 - 9:30', en: 'CLEANING', ja: '清掃', icon: 'bi-brush' },
  { time: '9:30 - 10:00', en: 'FOOD PREPARATION', ja: '朝食準備', icon: 'bi-egg-fried' },
  { time: '10:00 - 10:30', en: 'WARM UP', ja: 'ウォームアップ', icon: 'bi-lightning-charge-fill' },
  { time: '10:30 - 12:00', en: 'CORE SESSION 1', ja: 'コアセッション 1', icon: 'bi-mortarboard-fill' },
  { time: '12:00 - 13:30', en: 'LUNCH', ja: '昼食', icon: 'bi-cup-hot-fill' },
  { time: '13:30 - 13:45', en: 'BRUSHING TEETH', ja: '歯みがき', icon: 'bi-droplet-fill' },
  { time: '13:45 - 14:00', en: 'CLEANING', ja: '清掃', icon: 'bi-brush' },
  { time: '14:00 - 16:00', en: 'CORE SESSION 2', ja: 'コアセッション 2', icon: 'bi-mortarboard-fill' },
  { time: '16:00 - 17:30', en: 'BOARD GAMES', ja: 'ボードゲーム', icon: 'bi-dice-5-fill' },
];

const MILESTONES = [
  { pct: '25%', en: 'AWARD 1', ja: '表彰 1', icon: 'bi-star-fill', color: '#FFD166' },
  { pct: '50%', en: 'AWARD 2', ja: '表彰 2', icon: 'bi-stars', color: '#06D6A0' },
  { pct: '75%', en: 'AWARD 3', ja: '表彰 3', icon: 'bi-award-fill', color: '#118AB2' },
  { pct: '100%', en: 'AWARD 4', ja: '表彰 4', icon: 'bi-trophy-fill', color: '#FF6B35' },
];

function FloatingEmojis() {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {FLOATS.map((e, i) => (
        <span key={i} className="floating-emoji" style={{
          left: `${(i * 10 + 5) % 100}%`,
          animationDuration: `${8 + (i * 1.5) % 8}s`,
          animationDelay: `${(i * 0.7) % 5}s`,
          fontSize: `${1 + (i % 3) * 0.4}rem`,
        }}>{e}</span>
      ))}
    </div>
  );
}

export default function Landing() {
  const { t, lang, user } = useApp();
  const navigate = useNavigate();

  // No redirect — logged-in users can always view the homepage

  return (
    <div className="summer-bg">
      {/* Hero */}
      <div className="hero-bg" style={{ minHeight: '85vh', display: 'flex', alignItems: 'center', position: 'relative' }}>
        <FloatingEmojis />
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <div className="row align-items-center">
            <div className="col-lg-7 text-white">
              <div style={{ fontSize: '0.9rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', opacity: 0.85, marginBottom: '1rem' }}>
                🌺 Okinawa, Japan · 2026
              </div>
              <h1 style={{ fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.15, marginBottom: '1.5rem' }}>
                {t.welcome}
              </h1>
              <p style={{ fontSize: '1.2rem', opacity: 0.9, fontWeight: 600, marginBottom: '2.5rem', maxWidth: 520 }}>
                {t.welcomeSubtitle}
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a href="#programs" className="btn-summer text-decoration-none" style={{ fontSize: '1.1rem', padding: '0.9rem 2.5rem', background: 'white', color: 'var(--orange)' }}>
                  {t.programs} ↓
                </a>
              </div>
            </div>
            <div className="col-lg-5 d-none d-lg-flex justify-content-center align-items-center" style={{ gap: 24 }}>
              {/* Logo circle */}
              <div style={{
                width: 220, height: 220, borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                border: '3px solid rgba(255,255,255,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'bounce 3s ease-in-out infinite',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                flexShrink: 0,
              }}>
                <img src="/logo.png" alt="Ryukyu GLOCAL"
                  style={{ width: 175, height: 175, borderRadius: '50%', objectFit: 'contain' }} />
              </div>
              {/* Photo circle with winner tag */}
              <div style={{ position: 'relative', flexShrink: 0, animation: 'floatPhoto 4s ease-in-out infinite' }}>
                <div style={{
                  width: 270, height: 270, borderRadius: '50%',
                  overflow: 'hidden',
                  border: '5px solid rgba(255,255,255,0.7)',
                  boxShadow: '0 24px 70px rgba(0,0,0,0.25)',
                }}>
                  <img src="/assets/IMG_0113.jpg" alt="Summer School 2026 Phase 1 Winner"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
                {/* Winner badge */}
                <div style={{
                  position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #FFD700, #FF6B35)',
                  color: 'white', fontWeight: 900, fontSize: '0.72rem',
                  letterSpacing: '0.5px', whiteSpace: 'nowrap',
                  padding: '5px 16px', borderRadius: 999,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                  border: '2px solid rgba(255,255,255,0.6)',
                }}>
                  🏆 2026 Phase 1 Winner
                </div>
              </div>
              <style>{`
                @keyframes floatPhoto {
                  0%,100% { transform: translateY(0px) rotate(-1deg); }
                  50%      { transform: translateY(-18px) rotate(1deg); }
                }
              `}</style>
            </div>
          </div>
        </div>
      </div>

      {/* Wave */}
      <div className="wave-divider" style={{ background: 'linear-gradient(135deg, #FF6B35, #06D6A0)', height: 60, clipPath: 'ellipse(55% 100% at 50% 0%)' }} />

      {/* Programs */}
      <div className="container py-5" id="programs">
        <div className="text-center mb-5">
          <h2 style={{ fontWeight: 900, fontSize: '2.2rem', color: 'var(--navy)' }}>{t.programs}</h2>
          <p style={{ color: 'var(--muted)', fontWeight: 600 }}>
            {lang === 'ja' ? '6つのユニークなプログラムから選んで学ぼう！' : 'Choose from 6 unique programs and start learning!'}
          </p>
        </div>

        <div className="row g-4">
          {PROGRAMS.map(p => (
            <div key={p.slug} className="col-md-6 col-lg-4">
              <Link to="/curriculum" className="text-decoration-none">
                <div className="card-program" style={{ padding: 0 }}>
                  <div style={{ position: 'relative', width: '100%', aspectRatio: '1414 / 2000', overflow: 'hidden' }}>
                    <img
                      src={p.image}
                      alt={t[p.key]}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <div style={{
                      position: 'absolute', left: 0, right: 0, bottom: 0,
                      background: p.color, padding: '0.9rem 1.1rem',
                    }}>
                      <h4 style={{ fontWeight: 900, fontSize: '1.05rem', margin: 0, color: 'white' }}>
                        <span style={{ marginRight: 6 }}>{p.icon}</span>{t[p.key]}
                      </h4>
                      <div style={{ opacity: 0.9, fontSize: '0.75rem', fontWeight: 600, marginTop: 2, color: 'white' }}>
                        {lang === 'ja' ? '5日間のプログラム · 毎日スタンプ獲得！' : '5-day program · Earn daily stamps!'}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Daily Schedule */}
      <div style={{ background: 'linear-gradient(180deg, #FFF8F0, white)', padding: '5rem 0' }} id="schedule">
        <div className="container">
          <div className="text-center mb-5">
            <div style={{ fontSize: '2.4rem', color: 'var(--orange)', marginBottom: '0.5rem' }}>
              <i className="bi bi-calendar3" />
            </div>
            <h2 style={{ fontWeight: 900, fontSize: '2.2rem', color: 'var(--navy)' }}>
              {lang === 'ja' ? '1日のスケジュール' : 'Daily Schedule'}
            </h2>
            <p style={{ color: 'var(--muted)', fontWeight: 600 }}>
              {lang === 'ja' ? '毎日このスケジュールで活動します' : 'Every day follows this schedule'}
            </p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-8">
              {SCHEDULE.map((s, i) => (
                <div key={i} className="d-flex align-items-center gap-3" style={{
                  background: i % 2 === 0 ? 'white' : '#FFF8F0',
                  border: '1px solid #F1F5F9',
                  borderRadius: 16,
                  padding: '0.9rem 1.2rem',
                  marginBottom: '0.6rem',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                }}>
                  <div style={{
                    width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg, var(--orange), var(--coral))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '1.3rem',
                    boxShadow: '0 4px 12px rgba(255,107,53,0.3)',
                  }}>
                    <i className={`bi ${s.icon}`} />
                  </div>
                  <div style={{ fontWeight: 900, color: 'var(--orange)', fontSize: '0.95rem', minWidth: 130 }}>
                    {s.time}
                  </div>
                  <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                    {lang === 'ja' ? s.ja : s.en}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Gifts & Awards */}
      <div style={{ background: 'linear-gradient(135deg, #073B4C, #118AB2)', padding: '5rem 0', position: 'relative', overflow: 'hidden' }} id="gifts">
        <div className="container position-relative">
          <div className="text-center mb-5">
            <div style={{ fontSize: '2.4rem', color: '#FFD166', marginBottom: '0.5rem' }}>
              <i className="bi bi-gift-fill" />
            </div>
            <h2 style={{ fontWeight: 900, fontSize: '2.2rem', color: 'white' }}>
              {lang === 'ja' ? 'ギフト & 表彰' : 'Gifts and Awards'}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600, maxWidth: 640, margin: '0 auto' }}>
              {lang === 'ja'
                ? '進捗に応じて表彰があります。また、授業中のパフォーマンスに応じて特別な賞や景品がもらえることもあります！'
                : "Students receive awards as they progress. There are also special prizes and gifts during sessions, based on performance!"}
            </p>
          </div>

          <div className="row g-4 justify-content-center mb-4">
            {MILESTONES.map((m, i) => (
              <div key={i} className="col-6 col-md-3">
                <div style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 20,
                  padding: '1.75rem 1rem',
                  textAlign: 'center',
                  backdropFilter: 'blur(6px)',
                  height: '100%',
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%', margin: '0 auto 0.75rem',
                    background: m.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.5rem', color: 'white',
                    boxShadow: `0 6px 18px ${m.color}80`,
                  }}>
                    <i className={`bi ${m.icon}`} />
                  </div>
                  <div style={{ fontWeight: 900, fontSize: '1.5rem', color: 'white' }}>{m.pct}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.4rem' }}>
                    {lang === 'ja' ? '達成' : 'COMPLETE'}
                  </div>
                  <div style={{ fontWeight: 800, color: '#FFD166', fontSize: '0.95rem' }}>
                    {lang === 'ja' ? m.ja : m.en}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Certificates + Gift Card banner */}
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <div style={{
                background: 'white',
                borderRadius: 24,
                padding: '2rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem',
                flexWrap: 'wrap',
                boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: 18, flexShrink: 0,
                  background: 'linear-gradient(135deg, var(--orange), var(--coral))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.8rem', color: 'white',
                }}>
                  <i className="bi bi-patch-check-fill" />
                </div>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <h4 style={{ fontWeight: 900, color: 'var(--navy)', marginBottom: '0.4rem' }}>
                    {lang === 'ja' ? '修了証 CERTIFICATES' : 'CERTIFICATES'}
                  </h4>
                  <p style={{ color: 'var(--muted)', fontWeight: 600, marginBottom: '0.8rem' }}>
                    {lang === 'ja'
                      ? '修了証を受け取る際、パフォーマンスに応じて 1,500円〜5,000円分のギフトカードを選んでもらえます。'
                      : 'When students receive their final certificate, they also get to choose a gift card — worth ¥1,500–¥5,000, based on their performance.'}
                  </p>
                  <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>
                    <i className="bi bi-credit-card-2-front-fill" style={{ marginRight: 5 }} />
                    {lang === 'ja' ? 'ギフトカードの例（他にもあります）' : 'Gift card options (examples — more available)'}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#FFF0E0', color: 'var(--orange)', fontWeight: 800, fontSize: '0.8rem', padding: '0.4rem 0.9rem', borderRadius: 999 }}>
                      <i className="bi bi-gift-fill" /> Amazon
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#E0F7FA', color: '#0891b2', fontWeight: 800, fontSize: '0.8rem', padding: '0.4rem 0.9rem', borderRadius: 999 }}>
                      <i className="bi bi-controller" /> Roblox
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#E8F9F0', color: '#06a35a', fontWeight: 800, fontSize: '0.8rem', padding: '0.4rem 0.9rem', borderRadius: 999 }}>
                      <i className="bi bi-chat-fill" /> LINE
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#F1F5F9', color: '#64748b', fontWeight: 800, fontSize: '0.8rem', padding: '0.4rem 0.9rem', borderRadius: 999 }}>
                      <i className="bi bi-plus-circle-fill" /> {lang === 'ja' ? 'その他' : 'More'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div style={{ background: 'white', padding: '5rem 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontWeight: 900, fontSize: '2rem', color: 'var(--navy)' }}>
              {lang === 'ja' ? '毎日の体験' : 'Every Day Adventure'}
            </h2>
          </div>
          <div className="row g-4 justify-content-center">
            {[
              { icon: '⭐', title: lang === 'ja' ? 'スタンプを集めよう' : 'Collect Stamps', desc: lang === 'ja' ? '毎日午前・午後にスタンプを獲得！全10枚コンプリートを目指そう' : 'Earn morning & afternoon stamps each day! Collect all 10!' },
              { icon: '💡', title: lang === 'ja' ? 'アイデアを育てよう' : 'Grow Your Idea', desc: lang === 'ja' ? 'ビジネスアイデアを5日間かけて磨き上げる' : 'Develop your business idea over 5 exciting days' },
              { icon: '🏆', title: lang === 'ja' ? '修了証をもらおう' : 'Earn Certificate', desc: lang === 'ja' ? '修了証と特別表彰でプログラムを締めくくる' : 'Complete the program with a beautiful certificate & award' },
            ].map((f, i) => (
              <div key={i} className="col-md-4">
                <div className="text-center card-summer p-4">
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{f.icon}</div>
                  <h5 style={{ fontWeight: 800, marginBottom: '0.7rem' }}>{f.title}</h5>
                  <p style={{ color: 'var(--muted)', fontWeight: 600, fontSize: '0.9rem', margin: 0 }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center py-5" style={{ background: 'linear-gradient(135deg, #FFF0E0, #F0F8FF)' }}>
        <h3 style={{ fontWeight: 900, marginBottom: '0.5rem' }}>
          {lang === 'ja' ? '沖縄から世界へ 🌺' : 'From Okinawa to the World 🌺'}
        </h3>
        <p style={{ color: 'var(--muted)', fontWeight: 600, margin: 0 }}>Ryukyu GLOCAL Kids Summer School 2026</p>
      </div>

      {/* Footer */}
      <footer style={{ background: 'var(--navy)', color: 'rgba(255,255,255,0.7)', padding: '2rem', textAlign: 'center', fontSize: '0.85rem' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 68, height: 68, borderRadius: 18, background: 'white',
          marginBottom: '0.8rem', padding: 6, boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
        }}>
          <img src="/logo.png" alt="Ryukyu GLOCAL logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <div>Made in Okinawa by 琉球GLOCAL Tech &nbsp;|&nbsp; © 2026 株式会社琉球GLOCAL</div>
      </footer>
    </div>
  );
}
